<template>
  <div class="level-goal-repair-tab p-6">
    <div class="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold text-amber-900 mb-3">
        <i class="pi pi-wrench mr-2"></i>
        Duplicate goal ID repair
      </h2>
      <div class="text-amber-900 space-y-2 text-sm">
        <p>
          When two goals in the same level share the same <code class="bg-amber-100 px-1 rounded">id</code>,
          Development points updates can target the wrong goal. This tool scans <strong>all levels</strong>,
          assigns unique goal ids in <code class="bg-amber-100 px-1 rounded">level_targets</code>,
          fixes <code class="bg-amber-100 px-1 rounded">user_level_progress</code> for incomplete levels
          (assessment counts from submitted assessments, phantom progress removed where applicable),
          then recalculates <code class="bg-amber-100 px-1 rounded">User.currentLevel</code> for affected users.
        </p>
        <p class="font-medium">
          Run dry run first, review sample rows, then execute on the current tenant only.
        </p>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 mb-6">
      <Button
        label="Run dry run"
        icon="pi pi-search"
        :loading="loadingPreview"
        class="p-button-info"
        @click="runPreview"
      />
      <Button
        label="Execute repair"
        icon="pi pi-check"
        :loading="loadingExecute"
        :disabled="!hasPreviewResults"
        class="p-button-success"
        @click="runExecute"
      />
      <Button
        v-if="hasPreviewResults || hasExecuteResults"
        label="Clear results"
        icon="pi pi-times"
        class="p-button-secondary"
        :disabled="loadingPreview || loadingExecute"
        @click="clearResults"
      />
    </div>

    <div v-if="previewError" class="bg-red-50 border border-red-200 rounded p-4 mb-6 text-red-800">
      {{ previewError }}
    </div>

    <div v-if="executeError" class="bg-red-50 border border-red-200 rounded p-4 mb-6 text-red-800">
      {{ executeError }}
    </div>

    <div v-if="hasPreviewResults" class="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
      <h3 class="text-lg font-semibold text-slate-800 mb-4">Dry run summary</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div class="bg-white rounded p-4 border border-slate-100">
          <div class="text-sm text-slate-600">Levels with duplicate ids</div>
          <div class="text-2xl font-bold text-slate-800">
            {{ previewData.affectedLevels?.length || 0 }}
          </div>
        </div>
        <div class="bg-white rounded p-4 border border-slate-100">
          <div class="text-sm text-slate-600">User docs to update</div>
          <div class="text-2xl font-bold text-slate-800">
            {{ previewData.totalUserDocsToUpdate ?? 0 }}
          </div>
        </div>
      </div>

      <div v-if="previewData.affectedLevels?.length" class="mb-6">
        <h4 class="font-semibold text-slate-700 mb-2">Affected levels</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm border border-slate-200 bg-white rounded">
            <thead class="bg-slate-100">
              <tr>
                <th class="text-left p-2 border-b">Level</th>
                <th class="text-left p-2 border-b">Duplicate IDs</th>
                <th class="text-left p-2 border-b">ID renames</th>
                <th class="text-left p-2 border-b">User docs</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in previewData.affectedLevels" :key="row.level">
                <td class="p-2 border-b">{{ row.level }}</td>
                <td class="p-2 border-b font-mono text-xs">{{ row.duplicateIds?.join(', ') }}</td>
                <td class="p-2 border-b font-mono text-xs">
                  <div v-for="(r, i) in row.goalIdRenames" :key="i">
                    {{ r.from }} → {{ r.to }}
                  </div>
                </td>
                <td class="p-2 border-b">{{ row.userProgressCount }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="hasExecuteResults" class="bg-green-50 border border-green-200 rounded-lg p-6">
      <h3 class="text-lg font-semibold text-green-900 mb-4">Execute result</h3>
      <p class="mb-2">
        <strong>Levels updated:</strong> {{ executeData.levelsUpdated }},
        <strong>User documents updated:</strong> {{ executeData.userDocsUpdated }}
      </p>
      <div v-if="executeData.errors?.length" class="mt-4">
        <h4 class="font-semibold text-red-800 mb-2">Errors / warnings</h4>
        <ul class="list-disc list-inside text-sm text-red-800 space-y-1">
          <li v-for="(err, i) in executeData.errors" :key="i">{{ err }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import levelSystemService from '@/services/levelSystemService';

export default {
  name: 'LevelGoalRepairTab',
  components: { Button },
  data() {
    return {
      loadingPreview: false,
      loadingExecute: false,
      previewData: null,
      executeData: null,
      previewError: null,
      executeError: null,
    };
  },
  computed: {
    hasPreviewResults() {
      return this.previewData != null;
    },
    hasExecuteResults() {
      return this.executeData != null;
    },
  },
  methods: {
    async runPreview() {
      this.previewError = null;
      this.previewData = null;
      this.loadingPreview = true;
      try {
        const res = await levelSystemService.previewDuplicateGoalsRepair();
        this.previewData = res?.data ?? res;
      } catch (e) {
        this.previewError = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingPreview = false;
      }
    },
    async runExecute() {
      if (!this.hasPreviewResults) return;
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Apply duplicate goal ID repair on this tenant? This updates level_targets and user_level_progress.',
      );
      if (!ok) return;
      this.executeError = null;
      this.executeData = null;
      this.loadingExecute = true;
      try {
        const res = await levelSystemService.executeDuplicateGoalsRepair();
        this.executeData = res?.data ?? res;
      } catch (e) {
        this.executeError = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingExecute = false;
      }
    },
    clearResults() {
      this.previewData = null;
      this.executeData = null;
      this.previewError = null;
      this.executeError = null;
    },
  },
};
</script>
